<script lang="ts">
  import type { Occurrence, Place } from "../../types";
  import OpenStatus from "./open-status.svelte";
  import Hours from "./hours.svelte";
  import Sprite from "../sprite.svelte";
  import { getNow, getTimeZone } from "../../data/clock";
  import { weekdayRange } from "../../support/dates";
  import { onDay } from "../../domain/hours";
  import { getClosed } from "../../domain/status";
  import Locale from "../locale.svelte";
  import Weekday from "../weekday.svelte";

  export let timeZone = getTimeZone();
  export let time = getNow();
  export let schedule: Occurrence[];
  export let place: Place;

  let isExpanded = false;

  function toggle() {
    isExpanded = !isExpanded;
  }

  $: hasSchedule = schedule.length > 0;
  $: icon = isExpanded ? "icon-close" : "icon-clock";
  $: closed = getClosed(place, $time);
</script>

{#if !closed && hasSchedule}
  <h2 class="Summary type-body-2">
    <button
      id="hours-table-{place.id}-toggle"
      aria-expanded={isExpanded}
      aria-controls="hours-table-{place.id}"
      class="Toggle type-body-2"
      type="button"
      on:click={toggle}
    >
      <span class="TimeToday">
        <OpenStatus {place} {schedule} {time} />
      </span>
      <Sprite {icon} />
    </button>
  </h2>

  <div
    class="Details"
    id="hours-table-{place.id}"
    role="region"
    aria-labelledby="hours-table-{place.id}-toggle"
    hidden={!isExpanded}
  >
    <table class="type-body-2">
      <thead class="sr-only">
        <tr>
          <th class="Day">
            <Locale label="places.dayOfWeek" />
          </th>
          <th class="Hours">
            <Locale label="places.hours" />
          </th>
        </tr>
      </thead>
      <tbody>
        {#each weekdayRange($time) as date, dayIndex}
          <tr class:current={dayIndex == 0}>
            <td class="Day">
              {#if dayIndex == 0}
                <Weekday label="places.schedule.today" {date} {timeZone} />
              {:else}
                <Weekday label="places.schedule.otherDays" {date} {timeZone} />
              {/if}
            </td>
            <td class="Hours">
              <Hours {place} hours={onDay(schedule, date, $timeZone)} />
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}

<style>
  .current {
    font-weight: 600;
  }

  .Summary {
    border-bottom: 1px solid var(--color-tooling-opaque);
    font-size: 14px;
  }

  .Toggle {
    align-items: center;
    color: currentColor;
    background: none;
    border: 0;
    display: flex;
    font-size: inherit;
    padding-block: 8px;
    padding-left: max(env(safe-area-inset-left), var(--outer-padding));
    padding-right: max(env(safe-area-inset-right), var(--outer-padding));
    width: 100%;
  }

  .TimeToday {
    display: flex;
    flex: 1 1;
    text-align: left;
  }

  .Details {
    border-bottom: 1px solid var(--color-tooling-opaque);
    font-size: 14px;
    padding-block: 0;
    padding-left: max(env(safe-area-inset-left), 16px);
    padding-right: max(env(safe-area-inset-right), 16px);
  }

  table {
    border-collapse: collapse;
    width: 100%;
  }

  td {
    border-bottom: 1px dotted rgba(0, 0, 0, 0.2);
    padding: 8px 0;
    vertical-align: top;
  }

  tr:last-child td {
    border-bottom: 0;
  }

  .Day {
    text-align: left;
    text-transform: capitalize;
  }

  .Hours {
    text-align: right;
  }
</style>
